// src/components/store/sections/faqs/DynamicFaqVariant.tsx
"use client";

import React, { useState } from "react";
import { Plus, Minus, HelpCircle, Sparkles, CheckCircle2 } from "lucide-react";

const FALLBACK_FAQS = [
  {
    id: 1,
    question: "How fast is doorstep delivery across Bangladesh?",
    answer: "Inside Dhaka we deliver within 24 hours, and nationwide express delivery takes 48–72 hours with live tracking.",
  },
  {
    id: 2,
    question: "Can I check the product in front of the delivery rider?",
    answer: "Yes! We offer 100% open-box inspection before payment so you can shop with complete peace of mind.",
  },
  {
    id: 3,
    question: "What is your return and instant exchange policy?",
    answer: "You can request a free return or size exchange within 7 days of receiving your order with zero hassle.",
  },
  {
    id: 4,
    question: "Do you accept Cash on Delivery, bKash, and Nagad?",
    answer: "Absolutely. We support Cash on Delivery (COD), bKash, Nagad, Rocket, and all major debit/credit cards.",
  },
];

export default function DynamicFaqVariant({
  variantNumber,
  archKey,
  archLabel,
  palName,
  grad,
  lightBg,
  textCol,
  borderCol,
  faqs = [],
}: any) {
  const items = Array.isArray(faqs) && faqs.length > 0 ? faqs : FALLBACK_FAQS;
  const [openIdx, setOpenIdx] = useState<number | null>(0);
  const isTwoCol = archKey === "two_column_box" || archKey === "card_grid" || archKey === "bento_help";
  const isDark = archKey === "cyber_terminal" || palName === "SlateGold";

  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-6 py-6 select-none font-sans space-y-6">
      <div className="text-center space-y-1.5">
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full ${lightBg} ${textCol} text-[10px] font-black uppercase tracking-widest border ${borderCol}`}>
          <HelpCircle className="w-3.5 h-3.5" /> Variant {variantNumber}: {archLabel} ({palName})
        </span>
        <h2 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight">
          Frequently Asked Questions
        </h2>
      </div>

      <div className={isTwoCol ? "grid grid-cols-1 md:grid-cols-2 gap-4" : "space-y-3"}>
        {items.map((faq: any, idx: number) => {
          const isOpen = openIdx === idx;
          return (
            <div
              key={faq.id || idx}
              className={`rounded-2xl border-2 transition-all overflow-hidden ${
                isOpen
                  ? `bg-gradient-to-r ${grad} text-white border-transparent shadow-lg`
                  : isDark
                  ? "bg-slate-950 text-white border-slate-800"
                  : `bg-white text-slate-800 ${borderCol} hover:shadow-md`
              }`}
            >
              <button
                type="button"
                onClick={() => setOpenIdx(isOpen ? null : idx)}
                className="w-full px-5 py-4 flex items-center justify-between gap-3 text-left font-black text-xs sm:text-sm cursor-pointer"
              >
                <span className="flex items-center gap-2.5">
                  <span className={`w-6 h-6 rounded-lg flex items-center justify-center text-[10px] font-mono shrink-0 ${isOpen ? "bg-white/20 text-white" : `${lightBg} ${textCol}`}`}>
                    0{idx + 1}
                  </span>
                  <span>{faq.question}</span>
                </span>
                <span className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${isOpen ? "bg-white/20 text-white" : "bg-slate-100 text-slate-600"}`}>
                  {isOpen ? <Minus className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                </span>
              </button>
              {(isOpen || isTwoCol) && (
                <div className={`px-5 pb-4 pt-1 text-xs leading-relaxed border-t ${isOpen ? "border-white/15 text-white/90" : "border-slate-100 text-slate-500"}`}>
                  {faq.answer}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
